import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildConfigurableTooltipFormatter, buildConfigurableAxisLabel } from '../../utils/chart.utils';
import { buildSecondaryXAxisOption, buildSecondaryYAxisOption } from '../../utils/axis.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-line-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe],
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss',
})
export class LineChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected myChart!: echarts.ECharts;
  protected allCategories: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: { category: string[]; value: number[] };
  private showLabelsOnHoverActive = false;

  get lineChartHeaderStyle(): Record<string, string> {
    const backgroundColor = String(this.data?.headerBgColor || this.data?.cardHeaderBgColor || '').trim();
    const color = String(this.data?.headerTextColor || this.data?.cardHeaderTextColor || '').trim();

    return {
      ...(backgroundColor ? { '--dvm-card-header-bg': backgroundColor } : {}),
      ...(color ? { '--dvm-card-header-text': color } : {}),
      ...(backgroundColor ? { 'background-color': backgroundColor } : {}),
      ...(color ? { color } : {}),
    };
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => this.allCategories,
    });

    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init is required because ECharts' registered 'dvm' theme
    // updates do NOT propagate to already-initialized chart instances.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    if (this.data?.data) {
      this.renderChart(this.data.data);
      this.addResizeListener();
      return;
    }

    this.configService.getConfig(this.data.apiUrl, this.data, filters)?.subscribe({
      next: (res: any) => {
        if (!res) return;
        const dataKey = this.data?.dataKey;
        const raw = dataKey ? res?.[dataKey] : (res?.data || res);
        if (Array.isArray(raw)) {
          this.wrapper.setSourceData(raw, this.data?.categoryKey || 'category');
        }
        let chartData: any;
        if (Array.isArray(raw)) {
          const catKey = this.data?.categoryKey || 'category';
          const valKey = this.data?.valueKey || 'value';
          chartData = {
            category: raw.map((item: any) => item[catKey]),
            value: raw.map((item: any) => item[valKey]),
          };
        } else {
          chartData = raw;
        }
        this.renderChart(chartData);
        this.addResizeListener();
      },
      error: () => {
        this.myChart?.showLoading();
      },
    });
  }

  protected renderChart(chartData?: { category: string[]; value: number[] }): void {
    if (chartData) this.lastChartData = chartData;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    this.allCategories = chartData?.category || [];

    const chartConfig = this.resolveChartConfig();
    const tooltipCfg = chartConfig?.tooltip;
    const formatter = buildConfigurableTooltipFormatter(tooltipCfg);
    const showTooltip = tooltipCfg?.show !== false;
    const labelsAllowed = chartConfig?.showLabels !== false;
    const showDataLabels = labelsAllowed && (
      chartConfig?.showDataLabels === true ||
      chartConfig?.showLineLabels === true
    );
    const showDataLabelsOnHover = labelsAllowed && (
      !showDataLabels &&
      chartConfig?.showDataLabelsOnHover === true ||
      !showDataLabels &&
      chartConfig?.showLineLabelsOnHover === true
    );
    this.showLabelsOnHoverActive = showDataLabelsOnHover;
    const lineLabelColor = chartConfig?.lineLabelColor || theme.chart?.dataLabelOutside || theme.chart?.textColor || '#444';
    const lineLabelFontSize = Number(chartConfig?.lineLabelFontSize || 11);
    const lineLabelOffset = Array.isArray(chartConfig?.lineLabelOffset)
      ? chartConfig.lineLabelOffset
      : [0, -2];

    const categories = chartData?.category || [];
    const enableDoubleXAxis = this.data?.enableDoubleXAxis === true;
    const enableDoubleYAxis = this.data?.enableDoubleYAxis === true;

    const primaryXAxis: any = {
      type: 'category',
      data: categories,
      triggerEvent: true,
      axisLabel: { show: labelsAllowed },
    };
    const secondaryX = buildSecondaryXAxisOption(this.data, categories);
    if (secondaryX && !labelsAllowed) {
      secondaryX.axis.axisLabel = { ...(secondaryX.axis.axisLabel || {}), show: false };
    }
    const xAxisOption = secondaryX ? [primaryXAxis, secondaryX.axis] : primaryXAxis;

    const yAxisLabel = buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis);
    const primaryYAxis: any = {
      type: 'value',
      triggerEvent: true,
      axisLabel: {
        ...(yAxisLabel || {}),
        show: labelsAllowed && yAxisLabel?.show !== false,
      },
    };
    const secondaryY = buildSecondaryYAxisOption(this.data);
    if (secondaryY && !labelsAllowed) {
      secondaryY.axisLabel = { ...(secondaryY.axisLabel || {}), show: false };
    }
    const yAxisOption = secondaryY ? [primaryYAxis, secondaryY] : primaryYAxis;

    const option = {
      tooltip: {
        show: showTooltip,
        trigger: tooltipCfg?.trigger || 'axis',
        appendToBody: tooltipCfg?.appendToBody ?? true,
        confine: tooltipCfg?.confine ?? false,
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        ...(formatter ? { formatter } : {}),
      },
      grid: {
        left: '3%',
        right: enableDoubleYAxis ? '8%' : '4%',
        top: chartConfig?.gridTop || '22%',
        bottom: enableDoubleXAxis ? '12%' : '3%',
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: [
        {
          type: 'line',
          data: chartData?.value || [],
          smooth: true,
          lineStyle: { width: 2 },
          itemStyle: { color: theme.chart?.palette?.[1] || '#91cc75' },
          label: {
            show: showDataLabels && !showDataLabelsOnHover,
            position: 'top',
            distance: Number(chartConfig?.lineLabelDistance ?? 8),
            offset: lineLabelOffset,
            formatter: (params: any) => this.formatLineLabel(params?.value),
            color: lineLabelColor,
            fontSize: lineLabelFontSize,
          },
          emphasis: showDataLabelsOnHover ? {
            focus: 'series',
            label: {
              show: true,
              position: 'top',
              distance: Number(chartConfig?.lineLabelDistance ?? 8),
              offset: lineLabelOffset,
              formatter: (params: any) => this.formatLineLabel(params?.value),
              color: lineLabelColor,
              fontSize: lineLabelFontSize,
            },
          } : undefined,
        },
      ],
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    this.bindHoverLabelEvents();
    setTimeout(() => this.myChart.hideLoading(), 1000);

    this.wrapper.afterRender();
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  private bindHoverLabelEvents(): void {
    if (!this.myChart) {
      return;
    }

    this.myChart.off('mouseover');
    this.myChart.off('mouseout');
    this.myChart.off('globalout');

    if (!this.showLabelsOnHoverActive) {
      return;
    }

    this.myChart.on('mouseover', (params: any) => {
      if (params?.componentType !== 'series') {
        return;
      }
      this.toggleHoverLabels(true);
    });

    this.myChart.on('mouseout', (params: any) => {
      if (params?.componentType !== 'series') {
        return;
      }
      this.toggleHoverLabels(false);
    });

    this.myChart.on('globalout', () => {
      this.toggleHoverLabels(false);
    });
  }

  private toggleHoverLabels(show: boolean): void {
    const option = this.myChart?.getOption?.();
    const series = Array.isArray(option?.['series']) ? option['series'] : [];
    if (!series.length) {
      return;
    }

    const updatedSeries = series.map((item: any) => ({
      ...item,
      label: {
        ...(Array.isArray(item?.label) ? item.label[0] : item?.label),
        show,
        position: 'top',
        color: (Array.isArray(item?.label) ? item.label[0] : item?.label)?.color || '#444',
      },
    }));

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  private resolveChartConfig(): any {
    const dataConfig = this.data?.chartConfig;
    if (dataConfig && typeof dataConfig === 'object' && !Array.isArray(dataConfig)) {
      return dataConfig;
    }

    return this.data || {};
  }

  private formatLineLabel(value: unknown): string {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return value === null || value === undefined ? '' : String(value);
    }

    return `${Math.round(numericValue)}`;
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
